export type DateParts = {
  year: number;
  month: number;
  day: number;
};

export type MonthYearParts = {
  year: number;
  month: number;
};

export const formatIsoDate = ({ year, month, day }: DateParts): string => {
  const monthValue = String(month).padStart(2, "0");
  const dayValue = String(day).padStart(2, "0");
  return `${year}-${monthValue}-${dayValue}`;
};

export const parseIsoDate = (value: string): DateParts | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!match) {
    return null;
  }

  const year = Number.parseInt(match[1], 10);
  const month = Number.parseInt(match[2], 10);
  const day = Number.parseInt(match[3], 10);

  if (
    !Number.isFinite(year) ||
    !Number.isFinite(month) ||
    !Number.isFinite(day) ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31
  ) {
    return null;
  }

  return { year, month, day };
};

export const dateFromIso = (value: string, fallback = new Date()): Date => {
  const parsed = parseIsoDate(value);
  if (!parsed) {
    return fallback;
  }

  return new Date(parsed.year, parsed.month - 1, parsed.day);
};

export const dateToIso = (date: Date): string =>
  formatIsoDate({
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
  });

export const toDateInputValue = (value?: string | null): string => {
  if (!value?.trim()) {
    return "";
  }

  const trimmed = value.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return trimmed;
  }

  const date = new Date(trimmed);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return dateToIso(date);
};

export const clampDate = (date: Date, minDate?: Date, maxDate?: Date): Date => {
  const candidate = new Date(date);
  candidate.setHours(0, 0, 0, 0);

  if (minDate) {
    const min = new Date(minDate);
    min.setHours(0, 0, 0, 0);
    if (candidate < min) {
      return min;
    }
  }

  if (maxDate) {
    const max = new Date(maxDate);
    max.setHours(0, 0, 0, 0);
    if (candidate > max) {
      return max;
    }
  }

  return candidate;
};

export const formatMonthYearIso = ({ year, month }: MonthYearParts): string =>
  `${year}-${String(month).padStart(2, "0")}`;

export const monthYearFromDate = (date: Date): MonthYearParts => ({
  year: date.getFullYear(),
  month: date.getMonth() + 1,
});

export const clampMonthYear = (
  parts: MonthYearParts,
  maxDate: Date = new Date(),
): MonthYearParts => {
  const max = monthYearFromDate(maxDate);
  if (parts.year > max.year) {
    return max;
  }
  if (parts.year === max.year && parts.month > max.month) {
    return max;
  }
  return parts;
};

export const parseMonthYearIso = (value: string): MonthYearParts | null => {
  const match = /^(\d{4})-(\d{2})$/.exec(value.trim());
  if (!match) return null;

  const year = Number.parseInt(match[1], 10);
  const month = Number.parseInt(match[2], 10);

  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) {
    return null;
  }

  return { year, month };
};

export const isMonthYearIso = (value: string): boolean => parseMonthYearIso(value) !== null;

export const formatMonthYearDisplay = (value: string): string => {
  const parsed = parseMonthYearIso(value);
  if (!parsed) return value;

  const date = new Date(parsed.year, parsed.month - 1, 1);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
};

export const normalizeMonthYearFormValue = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return "";

  const monthYear = parseMonthYearIso(trimmed);
  if (monthYear) {
    return formatMonthYearIso(monthYear);
  }

  const fullDate = parseIsoDate(trimmed);
  if (fullDate) {
    return formatMonthYearIso({ year: fullDate.year, month: fullDate.month });
  }

  return "";
};
